import React, { useState, useEffect } from "react";
import { API_URL } from "../../utils/config";
import { tincPermis } from "../../utils/Permisos";
import { useUserContext } from "../../utils/UserContext";
import TeclaEscEnter from "../../utils/Teclaescenter";
import AjudaCataleg from "./AjudaCataleg";
import { BotoAjuda } from "./AjudaModal";

const UNITATS_HABITUALS = ["kW", "W", "V", "A", "dB(A)", "mm", "kg", "m³/h", "Pa", "bar", "l/s", "°C", "ut"];

function parseValor(valor) {
  const s = (valor || "").trim();
  const m = s.match(/^(.*?)\s*\(\s*(.+?)\s*-\s*(.+?)\s*\)\s*$/);
  if (m) {
    return { nominal: m[1].trim(), min: m[2].trim(), max: m[3].trim() };
  }
  return { nominal: s, min: "", max: "" };
}

function composeValor({ nominal, min, max }) {
  const n = (nominal || "").trim();
  const a = (min || "").trim();
  const b = (max || "").trim();
  if (n && a && b) return `${n} (${a} - ${b})`;
  if (a && b) return `(${a} - ${b})`;
  return n;
}

function filaDesDeAtribut(atribut) {
  const parsed = parseValor(atribut.ValorAtribut);
  return {
    idAtributsProductes: atribut.idAtributsProductes || null,
    NomAtribut: atribut.NomAtribut || "",
    UnitatMesura: atribut.UnitatMesura || "",
    nominal: parsed.nominal,
    min: parsed.min,
    max: parsed.max,
  };
}

function AtributsProducte({ producte, onCancel, subnav }) {
  const { rolUsuari } = useUserContext();
  const [files, setFiles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const [mostraAjuda, setMostraAjuda] = useState(false);

  useEffect(() => {
    if (!producte?.idProductes) return;
    const fetchAtributs = async () => {
      try {
        const response = await fetch(`${API_URL}/Atributsproducte.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idProductes: producte.idProductes }),
        });
        const data = await response.json();
        setFiles((data.success ? data.atributs || [] : []).map(filaDesDeAtribut));
        setIsEditing(false);
      } catch (error) {
        console.error("Error al carregar atributs:", error);
        setFiles([]);
      }
    };
    fetchAtributs();
  }, [producte]);

  const handleChange = (index, field, value) => {
    setFiles((prev) => prev.map((fila, i) => (i === index ? { ...fila, [field]: value } : fila)));
  };

  const handleAddRow = () => {
    setFiles((prev) => [...prev, { NomAtribut: "", UnitatMesura: "", nominal: "", min: "", max: "" }]);
  };

  const handleDelete = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const atributsNets = files
      .map((fila) => ({
        NomAtribut: (fila.NomAtribut || "").trim(),
        ValorAtribut: composeValor(fila),
        UnitatMesura: (fila.UnitatMesura || "").trim(),
      }))
      .filter((a) => a.NomAtribut || a.ValorAtribut || a.UnitatMesura);

    const incomplets = atributsNets.filter((a) => !a.NomAtribut || !a.ValorAtribut);
    if (incomplets.length > 0) {
      alert("Cada atribut ha de tenir nom i valor. La unitat i el rang són opcionals.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/Guardaratributs.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idProductes: producte.idProductes, atributs: atributsNets }),
      });
      const result = await response.json();
      if (result.success) {
        setIsEditing(false);
      } else {
        alert(`Error en guardar atributs: ${result.message}`);
      }
    } catch (error) {
      console.error("Error al guardar atributs:", error);
      alert("Error al processar la sol·licitud.");
    }
  };

  TeclaEscEnter(onCancel, isEditing ? handleSubmit : null);

  return (
    <div className="dv2-entity">
      {mostraAjuda && <AjudaCataleg pestanya="atributs" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className="dv2-pageHeader-icon" aria-hidden="true">
            📋
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">Atributs</h1>
            <p className="dv2-pageHeader-subtitle">
              {[producte?.Marca, producte?.Model].filter(Boolean).join(" ") || "Fitxa tècnica del producte"}
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen els Atributs" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {pucModificar && !isEditing && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={() => {
                setIsEditing(true);
                setFiles((prev) => (prev.length ? prev : [{ NomAtribut: "", UnitatMesura: "", nominal: "", min: "", max: "" }]));
              }}
            >
              Editar
            </button>
          )}
          {isEditing && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              Desar
            </button>
          )}
        </div>
      </div>
      {subnav}

      <div className="dv2-entity-body">
      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">1</span>
          <h2 className="dv2-card-title">Fitxa tècnica</h2>
        </div>
        {!isEditing ? (
          files.length === 0 ? (
            <p className="dv2-empty">Encara no hi ha atributs. Entra a editar per afegir potències, consums, etc.</p>
          ) : (
            <div className="dv2-specList">
              {files.map((fila, index) => (
                <div key={index} className="dv2-spec">
                  <span className="dv2-spec-name">{fila.NomAtribut || "—"}</span>
                  <span className="dv2-spec-value">
                    {composeValor(fila) || "—"}{" "}
                    {fila.UnitatMesura ? <span className="dv2-spec-unit">{fila.UnitatMesura}</span> : null}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : (
          <>
            <p className="dv2-pageHeader-subtitle" style={{ marginBottom: 12 }}>
              El valor pot ser un número o text. Si omples mínim i màxim, es desarà com a «3,5 (1,1 - 3,8)».
            </p>
            {files.map((fila, index) => (
              <div key={index} className="dv2-editorRow">
                <label className="dv2-field">
                  <span className="dv2-label">Atribut</span>
                  <input
                    type="text"
                    className="dv2-input"
                    placeholder="Potència frigorífica"
                    value={fila.NomAtribut}
                    onChange={(e) => handleChange(index, "NomAtribut", e.target.value)}
                  />
                </label>
                <label className="dv2-field">
                  <span className="dv2-label">Valor</span>
                  <input
                    type="text"
                    className="dv2-input"
                    placeholder="3,5"
                    value={fila.nominal}
                    onChange={(e) => handleChange(index, "nominal", e.target.value)}
                  />
                </label>
                <label className="dv2-field">
                  <span className="dv2-label">Mín.</span>
                  <input
                    type="text"
                    className="dv2-input"
                    placeholder="1,1"
                    value={fila.min}
                    onChange={(e) => handleChange(index, "min", e.target.value)}
                  />
                </label>
                <label className="dv2-field">
                  <span className="dv2-label">Màx.</span>
                  <input
                    type="text"
                    className="dv2-input"
                    placeholder="3,8"
                    value={fila.max}
                    onChange={(e) => handleChange(index, "max", e.target.value)}
                  />
                </label>
                <label className="dv2-field">
                  <span className="dv2-label">Unitat</span>
                  <input
                    type="text"
                    className="dv2-input"
                    list="dv2-unitats-atribut"
                    placeholder="kW"
                    maxLength={10}
                    value={fila.UnitatMesura}
                    onChange={(e) => handleChange(index, "UnitatMesura", e.target.value)}
                  />
                </label>
                <button type="button" className="dv2-btn" title="Esborrar atribut" onClick={() => handleDelete(index)}>
                  ✕
                </button>
              </div>
            ))}
            <datalist id="dv2-unitats-atribut">
              {UNITATS_HABITUALS.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
            <div style={{ marginTop: 12 }}>
              <button type="button" className="dv2-btn" onClick={handleAddRow}>
                Afegir atribut
              </button>
            </div>
          </>
        )}
      </section>
      </div>

      {isEditing && (
        <div className="dv2-entity-dock">
          <div className="dv2-footerActions">
            <button type="button" className="dv2-btn" onClick={() => setIsEditing(false)}>
              Cancel·lar
            </button>
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              Desar atributs
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AtributsProducte;
